(() => {
  const publishedElements = new WeakMap();
  const storageKey = 'shy_attribution_v1';
  const legacyPendingLeadKey = 'shy_pending_contact_lead_v1';
  const leadQueueKey = 'shy_contact_lead_queue_v2';
  const leadQueueLimit = 20;
  const urlParamNames = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'site_source_name',
    'placement',
    'campaign_id',
    'adset_id',
    'ad_id',
    'campaign_name',
    'adset_name',
    'ad_name',
    'fbclid',
    'gclid',
  ];
  const aiSourceRules = [
    { source: 'chatgpt.com', aliases: ['chatgpt', 'openai', 'searchgpt'], domains: ['chatgpt.com', 'chat.openai.com'] },
    { source: 'claude.ai', aliases: ['claude', 'anthropic'], domains: ['claude.ai', 'claude.anthropic.com'] },
    { source: 'gemini.google.com', aliases: ['gemini', 'google gemini', 'bard'], domains: ['gemini.google.com', 'bard.google.com'] },
    { source: 'google-ai-overview', aliases: ['google ai overview', 'google aio', 'google ai mode'], domains: [] },
    { source: 'chat.deepseek.com', aliases: ['deepseek'], domains: ['deepseek.com'] },
    { source: 'perplexity.ai', aliases: ['perplexity'], domains: ['perplexity.ai'] },
    { source: 'copilot.microsoft.com', aliases: ['copilot', 'microsoft copilot', 'bing chat'], domains: ['copilot.microsoft.com'], urlPatterns: [/^bing\.com\/chat(?:[/?#]|$)/] },
    { source: 'grok.com', aliases: ['grok', 'xai', 'x ai'], domains: ['grok.com', 'grok.x.ai', 'x.ai'] },
    { source: 'poe.com', aliases: ['poe'], domains: ['poe.com'] },
    { source: 'you.com', aliases: ['you', 'you ai'], domains: ['you.com'] },
    { source: 'chat.mistral.ai', aliases: ['mistral', 'le chat', 'mistral le chat'], domains: ['chat.mistral.ai'] },
    { source: 'meta.ai', aliases: ['meta ai', 'metaai'], domains: ['meta.ai'] },
    { source: 'kimi.com', aliases: ['kimi', 'moonshot kimi'], domains: ['kimi.com', 'kimi.moonshot.cn'] },
    { source: 'doubao.com', aliases: ['doubao', '\u8c46\u5305'], domains: ['doubao.com'] },
    { source: 'qwen.ai', aliases: ['qwen', 'qwen chat', 'tongyi', '\u901a\u4e49\u5343\u95ee'], domains: ['qwen.ai', 'chat.qwen.ai', 'tongyi.aliyun.com'] },
    { source: 'yuanbao.tencent.com', aliases: ['yuanbao', 'tencent yuanbao', '\u817e\u8baf\u5143\u5b9d'], domains: ['yuanbao.tencent.com'] },
    { source: 'wenxiaoyan.com', aliases: ['ernie', 'ernie bot', 'wenxin', 'wenxiaoyan', '\u6587\u5fc3\u4e00\u8a00', '\u6587\u5c0f\u8a00'], domains: ['wenxiaoyan.com', 'yiyan.baidu.com'] },
    { source: 'chatglm.cn', aliases: ['chatglm', 'zhipu', 'zhipu qingyan', '\u667a\u8c31\u6e05\u8a00'], domains: ['chatglm.cn'] },
    { source: 'n.cn', aliases: ['nami ai', '360 ai', '\u7eb3\u7c73 ai'], domains: ['n.cn', 'ai.360.com'] },
    { source: 'notebooklm.google.com', aliases: ['notebooklm', 'google notebooklm'], domains: ['notebooklm.google.com'] },
    { source: 'manus.im', aliases: ['manus'], domains: ['manus.im'] },
    { source: 'genspark.ai', aliases: ['genspark'], domains: ['genspark.ai'] },
    { source: 'felo.ai', aliases: ['felo'], domains: ['felo.ai'] },
    { source: 'phind.com', aliases: ['phind'], domains: ['phind.com'] },
    { source: 'andi.ai', aliases: ['andi', 'andi search'], domains: ['andi.ai', 'andisearch.com'] },
    { source: 'komo.ai', aliases: ['komo'], domains: ['komo.ai'] },
    { source: 'blackbox.ai', aliases: ['blackbox', 'blackbox ai'], domains: ['blackbox.ai'] },
    { source: 'monica.im', aliases: ['monica', 'monica ai'], domains: ['monica.im'] },
    { source: 'character.ai', aliases: ['character ai', 'characterai'], domains: ['character.ai'] },
    { source: 'pi.ai', aliases: ['pi ai', 'inflection pi'], domains: ['pi.ai'] },
    { source: 'chatsonic.com', aliases: ['chatsonic'], domains: ['chatsonic.com'] },
    { source: 'huggingchat', aliases: ['huggingchat'], domains: [], urlPatterns: [/^huggingface\.co\/chat(?:[/?#]|$)/] },
    { source: 'brave-leo', aliases: ['brave leo', 'leo ai'], domains: [] },
    { source: 'opera-aria', aliases: ['opera aria', 'aria ai'], domains: [] },
  ];

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  }

  function safeStorage(method, key, value) {
    try {
      if (method === 'get') return window.localStorage.getItem(key);
      if (method === 'set') window.localStorage.setItem(key, value);
      if (method === 'remove') window.localStorage.removeItem(key);
    } catch {
      return null;
    }
    return null;
  }

  function normalizeParam(value, maxLength = 120) {
    return String(value || '').replace(/\s+/g, ' ').trim().slice(0, maxLength);
  }

  function metaSourceName(value) {
    const source = normalizeParam(value, 30).toLowerCase();
    if (source === 'fb') return 'facebook';
    if (source === 'ig') return 'instagram';
    if (source === 'msg') return 'messenger';
    if (source === 'an') return 'audience_network';
    return source;
  }

  function normalizeSourceName(value) {
    const rawSource = normalizeParam(value, 500).toLowerCase();
    let source = rawSource;
    let hostname = '';
    let path = '';
    try {
      const looksLikeHost = /^[a-z0-9.-]+\.[a-z]{2,}(?:[/?#]|$)/i.test(source);
      if (/^https?:\/\//.test(source) || looksLikeHost) {
        const parsed = new URL(/^https?:\/\//.test(source) ? source : `https://${source}`);
        hostname = parsed.hostname.replace(/^www\./, '').toLowerCase();
        path = `${parsed.pathname}${parsed.search}${parsed.hash}`.toLowerCase();
        source = hostname;
      }
    } catch {
      // Keep the original source value when it is not a valid URL.
    }

    const alias = rawSource.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim();
    const urlValue = `${hostname}${path}`;
    const matched = aiSourceRules.find((rule) =>
      rule.aliases.includes(alias) ||
      rule.domains.some((domain) => hostname === domain || hostname.endsWith(`.${domain}`)) ||
      (rule.urlPatterns || []).some((pattern) => pattern.test(urlValue))
    );
    if (matched) return matched.source;
    return source;
  }

  function isAiSource(source) {
    return aiSourceRules.some((rule) => rule.source === source);
  }

  function externalReferrer() {
    if (!document.referrer) return '';
    try {
      const hostname = new URL(document.referrer).hostname.replace(/^www\./, '').toLowerCase();
      const currentHost = window.location.hostname.replace(/^www\./, '').toLowerCase();
      const shopHost = String(window.Shopify?.shop || '').replace(/^www\./, '').toLowerCase();
      if (hostname === currentHost || hostname === shopHost) return '';
      return document.referrer;
    } catch {
      return '';
    }
  }

  function referrerSource() {
    const referrer = externalReferrer();
    if (!referrer) return '';
    try {
      const hostname = new URL(referrer).hostname.replace(/^www\./, '').toLowerCase();
      const aiSource = normalizeSourceName(referrer);
      if (isAiSource(aiSource)) return aiSource;
      if (hostname.includes('instagram.com')) return 'instagram';
      if (hostname.includes('facebook.com') || hostname.includes('fb.com')) return 'facebook';
      if (hostname.includes('google.')) return 'google';
      return hostname;
    } catch {
      return '';
    }
  }

  function readAttributionFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const values = {};
    urlParamNames.forEach((name) => {
      values[name] = normalizeParam(params.get(name));
    });

    const source = normalizeSourceName(values.utm_source || metaSourceName(values.site_source_name) || (values.fbclid ? 'meta' : '') || (values.gclid ? 'google' : '') || referrerSource() || 'direct');
    const medium = normalizeParam(values.utm_medium || (values.fbclid ? 'paid_social' : '') || (values.gclid ? 'paid_search' : '') || (isAiSource(source) ? 'ai_referral' : '') || (externalReferrer() ? 'referral' : 'direct'), 80);

    return {
      source,
      medium,
      campaign: normalizeParam(values.utm_campaign || values.campaign_name || values.campaign_id, 120),
      content: normalizeParam(values.utm_content || values.ad_name || values.ad_id, 120),
      term: normalizeParam(values.utm_term, 120),
      site_source_name: metaSourceName(values.site_source_name),
      placement: normalizeParam(values.placement, 120),
      campaign_id: normalizeParam(values.campaign_id, 80),
      adset_id: normalizeParam(values.adset_id, 80),
      ad_id: normalizeParam(values.ad_id, 80),
      landing_page: `${window.location.pathname}${window.location.search}`.slice(0, 500),
      referrer: externalReferrer().slice(0, 500),
      captured_at: new Date().toISOString(),
    };
  }

  function loadAttribution() {
    const stored = safeStorage('get', storageKey);
    if (!stored) return {};
    try {
      return JSON.parse(stored) || {};
    } catch {
      return {};
    }
  }

  function saveAttribution() {
    const current = readAttributionFromUrl();
    const stored = loadAttribution();
    const searchParams = new URLSearchParams(window.location.search);
    const hasUrlAttribution = window.location.search && urlParamNames.some((name) => searchParams.has(name));
    const hasReferrer = Boolean(externalReferrer());
    const shouldUpdateLast = hasUrlAttribution || hasReferrer || !stored.last_touch;
    const attribution = {
      first_touch: stored.first_touch || current,
      last_touch: shouldUpdateLast ? current : stored.last_touch,
    };
    safeStorage('set', storageKey, JSON.stringify(attribution));
    return attribution;
  }

  const attribution = saveAttribution();

  function cleanDestination(value) {
    if (!value) return '';
    try {
      const url = new URL(value, window.location.origin);
      if (url.hostname.includes('whatsapp.com') || url.hostname === 'wa.me') {
        return 'whatsapp';
      }
      return `${url.origin}${url.pathname}`;
    } catch {
      return '';
    }
  }

  function pageContext() {
    const main = document.querySelector('main[data-template]');
    const parts = window.location.pathname.split('/').filter(Boolean);
    return {
      page_type: main?.dataset.template || '',
      page_path: window.location.pathname,
      page_title: document.title.slice(0, 160),
      resource_handle: parts.length > 1 ? parts[parts.length - 1] : '',
    };
  }

  function attributionPayload() {
    const latest = loadAttribution();
    const firstTouch = latest.first_touch || attribution.first_touch || {};
    const lastTouch = latest.last_touch || attribution.last_touch || {};
    return {
      source: lastTouch.source || '',
      medium: lastTouch.medium || '',
      campaign: lastTouch.campaign || '',
      content: lastTouch.content || '',
      term: lastTouch.term || '',
      traffic_source: lastTouch.source || '',
      traffic_medium: lastTouch.medium || '',
      traffic_campaign: lastTouch.campaign || '',
      ad_content: lastTouch.content || '',
      site_source_name: lastTouch.site_source_name || '',
      placement: lastTouch.placement || '',
      campaign_id: lastTouch.campaign_id || '',
      adset_id: lastTouch.adset_id || '',
      ad_id: lastTouch.ad_id || '',
      landing_page: lastTouch.landing_page || '',
      referrer: lastTouch.referrer || '',
      first_source: firstTouch.source || '',
      first_medium: firstTouch.medium || '',
      first_campaign: firstTouch.campaign || '',
      first_traffic_source: firstTouch.source || '',
      first_traffic_medium: firstTouch.medium || '',
      first_traffic_campaign: firstTouch.campaign || '',
      first_landing_page: firstTouch.landing_page || '',
      first_captured_at: firstTouch.captured_at || '',
    };
  }

  function inferIntent(element, text) {
    const explicitIntent = element.dataset.shyIntent;
    if (explicitIntent) return explicitIntent;
    const areaText = normalizeText(element.closest('section, product-info, footer, header')?.textContent).toLowerCase();
    const combined = `${text.toLowerCase()} ${areaText}`;
    if (combined.includes('sample')) return 'sample';
    if (combined.includes('color match') || combined.includes('colour match')) return 'color_match';
    if (combined.includes('wholesale') || combined.includes('private label')) return 'wholesale';
    if (combined.includes('product')) return 'product';
    return 'general';
  }

  function inferClickEvent(element) {
    const explicitEvent = element.dataset.shyEvent;
    if (explicitEvent) return explicitEvent;

    const text = normalizeText(element.textContent || element.getAttribute('aria-label')).toLowerCase();
    const href = element.getAttribute('href') || '';
    const hrefLower = href.toLowerCase();

    if (hrefLower.includes('wa.me/') || hrefLower.includes('whatsapp.com/')) return 'whatsapp_click';
    if (text.includes('sample')) return 'sample_request';
    if (text.includes('color match') || text.includes('colour match')) return 'color_match_request';
    if (text.includes('wholesale') || text.includes('private label')) return 'wholesale_inquiry';
    if (text.includes('request a quote') || text.includes('request quote') || text.includes('get quote') || text.includes('ask for quote')) return 'quote_request';
    return '';
  }

  function publish(eventName, details = {}) {
    if (!eventName) return;
    const payload = {
      ...pageContext(),
      ...attributionPayload(),
      ...details,
      event_version: '1.0',
      event_timestamp: new Date().toISOString(),
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
    window.dispatchEvent(new CustomEvent('shy:conversion', { detail: { eventName, payload } }));

    const publisher = window.Shopify?.analytics?.publish;
    if (typeof publisher === 'function') {
      Promise.resolve(publisher(`shy:${eventName}`, payload)).catch(() => {});
    }
  }

  function cleanContactFieldName(name) {
    return String(name || '')
      .replace(/^contact\[/, '')
      .replace(/\]$/, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80);
  }

  function readContactForm(form) {
    const fields = {};
    const formData = new FormData(form);
    formData.forEach((value, name) => {
      if (!String(name).startsWith('contact[')) return;
      const key = cleanContactFieldName(name);
      if (!key) return;
      const normalized = String(value || '').replace(/\s+/g, ' ').trim().slice(0, 1000);
      if (!normalized) return;
      fields[key] = fields[key] ? `${fields[key]}; ${normalized}` : normalized;
    });

    const email = fields.email || fields.Email || '';
    const phone = fields.Phone || fields.phone || fields['WhatsApp or phone'] || '';
    const name = fields.Name || fields.name || '';

    return {
      lead_id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      store: window.Shopify?.shop || window.location.hostname,
      form_id: form.id || '',
      form_action: form.getAttribute('action') || '',
      name,
      email,
      phone,
      fields,
      page: {
        ...pageContext(),
        page_url: window.location.href.slice(0, 1000),
      },
      attribution: attributionPayload(),
      submitted_at: new Date().toISOString(),
    };
  }

  function leadRelayEndpoint(form) {
    if (!form || form.dataset.shyLeadCaptureEnabled !== 'true') return '';
    const endpoint = String(form.dataset.shyLeadEndpoint || '').trim();
    if (!/^https:\/\//i.test(endpoint)) return '';
    return endpoint;
  }

  function loadLeadQueue() {
    let queue = [];
    const stored = safeStorage('get', leadQueueKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) queue = parsed;
      } catch {
        queue = [];
      }
    }

    const legacy = safeStorage('get', legacyPendingLeadKey);
    if (legacy) {
      try {
        const parsed = JSON.parse(legacy);
        if (parsed?.endpoint && parsed?.lead?.lead_id && !queue.some((item) => item.lead?.lead_id === parsed.lead.lead_id)) {
          queue.push({ ...parsed, state: 'attempted', attempts: 1 });
        }
      } catch {
        // Ignore malformed data from the previous single-lead implementation.
      }
      safeStorage('remove', legacyPendingLeadKey);
    }

    return queue
      .filter((item) => item?.endpoint && item?.lead?.lead_id)
      .slice(-leadQueueLimit);
  }

  function saveLeadQueue(queue) {
    safeStorage('set', leadQueueKey, JSON.stringify(queue.slice(-leadQueueLimit)));
  }

  function updateQueuedLead(leadId, changes) {
    const queue = loadLeadQueue();
    const item = queue.find((candidate) => candidate.lead.lead_id === leadId);
    if (!item) return;
    Object.assign(item, changes);
    saveLeadQueue(queue);
  }

  function removeQueuedLead(leadId) {
    saveLeadQueue(loadLeadQueue().filter((item) => item.lead.lead_id !== leadId));
  }

  function relayLead(endpoint, payload) {
    const body = JSON.stringify(payload);
    return fetch(endpoint, {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      keepalive: true,
      mode: 'no-cors',
    }).then(() => true).catch(() => {
      if (!navigator.sendBeacon) return false;
      const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      return navigator.sendBeacon(endpoint, blob);
    });
  }

  function queueAndRelayLead(form) {
    const endpoint = leadRelayEndpoint(form);
    if (!endpoint) return;
    const lead = readContactForm(form);
    const attemptedAt = new Date().toISOString();
    const queue = loadLeadQueue();
    queue.push({ endpoint, lead, state: 'attempted', attempts: 1, last_attempt_at: attemptedAt });
    saveLeadQueue(queue);

    relayLead(endpoint, {
      event: 'shopify_contact_form_attempt',
      lead: { ...lead, status: 'awaiting_shopify_confirmation' },
      attempted_at: attemptedAt,
    }).then((delivered) => {
      updateQueuedLead(lead.lead_id, { attempt_delivered: delivered });
    });
  }

  function retryQueuedLeads() {
    const queue = loadLeadQueue();
    const now = Date.now();
    queue.forEach((item) => {
      if (item.state !== 'attempted' || Number(item.attempts || 0) >= 5) return;
      const lastAttempt = Date.parse(item.last_attempt_at || '') || 0;
      if (now - lastAttempt < 15000) return;

      item.attempts = Number(item.attempts || 0) + 1;
      item.last_attempt_at = new Date().toISOString();
      relayLead(item.endpoint, {
        event: 'shopify_contact_form_attempt',
        lead: { ...item.lead, status: 'awaiting_shopify_confirmation' },
        attempted_at: item.lead.submitted_at,
        retry: item.attempts,
      }).then((delivered) => {
        updateQueuedLead(item.lead.lead_id, { attempt_delivered: delivered });
      });
    });
    saveLeadQueue(queue);
  }

  function flushSuccessfulLead(successElement) {
    if (!successElement) return;
    const queue = loadLeadQueue();
    const item = [...queue].reverse().find((candidate) => candidate.state === 'attempted');
    if (!item) return;

    const confirmedAt = new Date().toISOString();
    item.state = 'confirmed';
    item.confirmed_at = confirmedAt;
    saveLeadQueue(queue);

    relayLead(item.endpoint, {
      event: 'shopify_contact_form_success',
      lead: { ...item.lead, status: 'confirmed_by_shopify' },
      attempted_at: item.lead.submitted_at,
      confirmed_at: confirmedAt,
    }).then((delivered) => {
      if (delivered) removeQueuedLead(item.lead.lead_id);
    });
  }

  function prepareWhatsappLink(element) {
    const href = element.getAttribute('href') || '';
    if (!href.toLowerCase().includes('wa.me/') && !href.toLowerCase().includes('whatsapp.com/')) return;
    try {
      const url = new URL(href, window.location.origin);
      if (!url.searchParams.has('text')) {
        url.searchParams.set('text', 'Hello, I would like to ask about SHY Hair. I am interested in your human hair wigs');
      }
      element.setAttribute('href', url.toString());
    } catch {
      return;
    }
  }

  document.addEventListener('click', (event) => {
    const element = event.target.closest('a, button');
    if (!element) return;
    prepareWhatsappLink(element);

    const eventName = inferClickEvent(element);
    if (!eventName) return;

    const now = Date.now();
    if (now - (publishedElements.get(element) || 0) < 800) return;
    publishedElements.set(element, now);

    const text = normalizeText(element.textContent || element.getAttribute('aria-label'));
    publish(eventName, {
      intent: inferIntent(element, text),
      element_text: text,
      destination: cleanDestination(element.getAttribute('href')),
      section_id: element.closest('.shopify-section')?.id || '',
    });
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form');
    if (!form || form.id !== 'ContactForm') return;
    const inquiryType = normalizeText(form.querySelector?.('[name="contact[Inquiry type]"]')?.value);
    const productReference = normalizeText(form.querySelector?.('[name="contact[Product reference]"]')?.value);
    queueAndRelayLead(form);
    publish(form.dataset.shyEvent || 'quote_form_submit', {
      intent: form.dataset.shyIntent || 'contact',
      form_id: form.id,
      inquiry_type: inquiryType,
      has_product_reference: Boolean(productReference),
      product_reference: productReference,
    });
  });

  document.addEventListener('DOMContentLoaded', () => {
    const productField = document.querySelector('#ContactForm-product-reference');
    const productNotice = document.querySelector('[data-contact-product-reference]');
    const productReference = normalizeText(new URLSearchParams(window.location.search).get('product'));
    if (productField && productReference) {
      productField.value = productReference;
      if (productNotice) {
        productNotice.textContent = `Product reference included: ${productReference.replace(/-/g, ' ')}`;
        productNotice.hidden = false;
      }
    }

    const success = document.querySelector('[data-shy-contact-success]');
    if (success) {
      publish('quote_form_success', {
        intent: success.dataset.shyIntent || 'contact',
        form_id: 'ContactForm',
      });
      flushSuccessfulLead(success);
    } else {
      retryQueuedLeads();
    }
  }, { once: true });
})();
